import { useState, useEffect } from 'react'
import { Info, Plus, Trash } from '@phosphor-icons/react'
import { startOfDay, endOfDay, subMinutes, addMinutes } from 'date-fns'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'
import DateField from '../../../../FormLegacy/FormFieldsV2/DateField'
import { daysOfWeek } from '../../../../../helpers/constants'
import {
  dateChangeRaw,
  isIntervalValid,
  onChangeNumberField,
  timeToInteger,
  integerToTime,
  DEFAULT_TIME_INTERVAL,
  getMidnightTime,
} from '../helpers'
import { dayToTimestamp, timestampToDay } from '../../../../../helpers/helpers'

const HourlyPricingSelector = ({ values, errors, setFieldValue = () => {}, resetKey }) => {
  const { t } = useTranslation(['reservations'])
  const [selectedDays, setSelectedDays] = useState([])
  const [daySchedules, setDaySchedules] = useState({})
  const [isInitialized, setIsInitialized] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})

  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      setSelectedDays([])
      setDaySchedules({})
      setIsInitialized(false)
      setFieldErrors({})
    }
  }, [resetKey])

  // Initialize state from existing hourlySlots values
  useEffect(() => {
    if (values.hourlySlots && values.hourlySlots.length > 0 && !isInitialized) {
      const newSelectedDays = []
      const newDaySchedules = {}

      values.hourlySlots.forEach((slot) => {
        const day = timestampToDay(slot.startDate)

        if (day) {
          // Add day to selected days if not already there
          if (!newSelectedDays.includes(day)) {
            newSelectedDays.push(day)
            newDaySchedules[day] = []
          }

          // Add interval to the day's schedule array
          newDaySchedules[day].push({
            startTime: integerToTime(slot.startTime),
            endTime: integerToTime(slot.endTime),
            price: slot.price?.toString() || '',
          })
        }
      })

      if (newSelectedDays.length > 0) {
        setSelectedDays(newSelectedDays)
        setDaySchedules(newDaySchedules)
        setIsInitialized(true)
      }
    } else if (!values.hourlySlots || values.hourlySlots.length === 0) {
      // If no hourlySlots are provided, mark as initialized to prevent infinite checking
      setIsInitialized(true)
    }
  }, [values.hourlySlots, isInitialized])

  const getFieldErrors = (daySchedules) => {
    const errors = {}

    Object.keys(daySchedules).forEach((day) => {
      const intervals = daySchedules[day] || []

      intervals.forEach((interval, index) => {
        const key = `${day}-${index}`
        const intervalErrors = {}

        // Check start time
        if (!interval.startTime) {
          intervalErrors.startTime = true
        }

        // Check end time
        if (!interval.endTime) {
          intervalErrors.endTime = true
        }

        // Check if end time is after start time
        if (
          interval.startTime &&
          interval.endTime &&
          timeToInteger(interval.startTime) >= timeToInteger(interval.endTime)
        ) {
          intervalErrors.endTime = true
        }

        // Check interval alignment with timeInterval setting
        if (interval.startTime && interval.endTime) {
          const isValid = isIntervalValid(
            interval.startTime,
            interval.endTime,
            values.timeInterval || DEFAULT_TIME_INTERVAL,
          )
          if (!isValid) {
            intervalErrors.endTime = true
          }
        }

        // Check for overlaps with previous interval (allow startTime to equal prevInterval.endTime)
        if (index > 0) {
          const prevInterval = intervals[index - 1]
          if (
            interval.startTime &&
            prevInterval.endTime &&
            timeToInteger(interval.startTime) < timeToInteger(prevInterval.endTime)
          ) {
            intervalErrors.startTime = true
          }
        }

        // Check price
        if (!interval.price || parseFloat(interval.price) < 0) {
          intervalErrors.price = true
        }

        if (Object.keys(intervalErrors).length > 0) {
          errors[key] = intervalErrors
        }
      })
    })

    return errors
  }

  // Function to update hourlySlots based on daySchedules
  const updateHourlySlots = () => {
    const hourlySlots = []

    selectedDays.forEach((day) => {
      const intervals = daySchedules[day] || []

      // Convert each interval to a slot
      intervals.forEach((interval) => {
        if (interval.startTime && interval.endTime) {
          hourlySlots.push({
            startDate: dayToTimestamp(day),
            startTime: timeToInteger(interval.startTime),
            endTime: timeToInteger(interval.endTime, true),
            repeats: true,
            price: interval.price ? parseFloat(interval.price) : null,
            weekdays: [daysOfWeek.indexOf(day)],
          })
        }
      })
    })

    const sortedHourlySlots = hourlySlots.sort((a, b) => {
      // Sort by day first, then by start time
      if (a.startDate === b.startDate) {
        return a.startTime - b.startTime
      }
      return a.startDate - b.startDate
    })
    const sortedExistingSlots = (values.hourlySlots || []).sort((a, b) => {
      if (a.startDate === b.startDate) {
        return a.startTime - b.startTime
      }
      return a.startDate - b.startDate
    })

    // check if items truly changed
    if (!isEqual(sortedHourlySlots, sortedExistingSlots)) {
      setFieldValue('hourlySlots', hourlySlots)
    }

    const newDaysAvailable = selectedDays.map((day) => daysOfWeek.indexOf(day))
    const sortedNewDays = [...newDaysAvailable].sort((a, b) => a - b)
    const sortedExistingDays = [...(values.daysAvailable || [])].sort((a, b) => a - b)
    if (!isEqual(sortedNewDays, sortedExistingDays)) {
      setFieldValue('daysAvailable', newDaysAvailable)
    }
  }

  // Update hourlySlots whenever selectedDays or daySchedules change (but not during initialization)
  useEffect(() => {
    if (isInitialized) {
      updateHourlySlots()
      const newFieldErrors = getFieldErrors(daySchedules)
      setFieldErrors(newFieldErrors)
    }
  }, [selectedDays, daySchedules, isInitialized])

  const handleSelectDay = (day) => {
    const isActive = selectedDays.includes(day)
    if (isActive) {
      // Remove day from selected days
      setSelectedDays(selectedDays.filter((selectedDay) => selectedDay !== day))
      const newDaySchedules = { ...daySchedules }
      delete newDaySchedules[day]
      setDaySchedules(newDaySchedules)
    } else {
      // Add day with one empty interval
      setSelectedDays([...selectedDays, day])
      setDaySchedules({
        ...daySchedules,
        [day]: [
          {
            startTime: '',
            endTime: '',
            price: '',
          },
        ],
      })
    }
  }

  const handleAddInterval = (day) => {
    const intervals = daySchedules[day] || []
    setDaySchedules({
      ...daySchedules,
      [day]: [
        ...intervals,
        {
          startTime: '',
          endTime: '',
          price: '',
        },
      ],
    })
  }

  const handleRemoveInterval = (day, intervalIndex) => {
    const intervals = daySchedules[day] || []

    // Don't allow removing the first interval - only additional ones can be removed
    if (intervalIndex === 0) {
      return
    }

    const newIntervals = intervals.filter((_, index) => index !== intervalIndex)

    setDaySchedules({
      ...daySchedules,
      [day]: newIntervals,
    })
  }

  const handleTimeChange = (day, intervalIndex, field, value) => {
    const intervals = [...(daySchedules[day] || [])]
    intervals[intervalIndex] = {
      ...intervals[intervalIndex],
      [field]: value,
    }
    setDaySchedules({
      ...daySchedules,
      [day]: intervals,
    })
  }

  const handlePriceChange = (day, intervalIndex, value) => {
    const intervals = [...(daySchedules[day] || [])]
    intervals[intervalIndex] = {
      ...intervals[intervalIndex],
      price: value,
    }
    setDaySchedules({
      ...daySchedules,
      [day]: intervals,
    })
  }

  const onStartTimeChange = (day, intervalIndex, value) => {
    const intervals = daySchedules[day] || []
    const interval = intervals[intervalIndex]

    if (isIntervalValid(value, interval?.endTime, values.timeInterval || DEFAULT_TIME_INTERVAL)) {
      handleTimeChange(day, intervalIndex, 'startTime', value)
    } else {
      // Clear end time if it's no longer valid
      const newIntervals = [...intervals]
      newIntervals[intervalIndex] = {
        ...newIntervals[intervalIndex],
        startTime: value,
        endTime: '',
      }
      setDaySchedules({
        ...daySchedules,
        [day]: newIntervals,
      })
    }
  }

  return (
    <div className="pricing-container">
      <div className="form-step__label">
        {t('reservations:amenityForms.pricingSelectors.hourlyPricingRequired')}
      </div>
      <div className="pricing-container__info">
        <Info size={16} />
        <span>{t('reservations:amenityForms.pricingSelectors.clickWeekdayToggle')}</span>
      </div>

      <div className="pricing-container__weekdays">
        {daysOfWeek.map((day) => {
          const isActive = selectedDays.includes(day)
          const intervals = daySchedules[day] || []

          return (
            <div key={day} className="weekday-row-container">
              <div className="weekday-row">
                <button
                  type="button"
                  className={`weekday-button ${
                    isActive ? 'weekday-button--active' : 'weekday-button--inactive'
                  }`}
                  onClick={() => handleSelectDay(day)}
                >
                  {day}
                </button>
                {isActive ? (
                  <div className="weekday-intervals">
                    {intervals.map((interval, intervalIndex) => {
                      const prevInterval = intervalIndex > 0 ? intervals[intervalIndex - 1] : null
                      const isFirstInterval = intervalIndex === 0
                      const errorKey = `${day}-${intervalIndex}`
                      const intervalErrors = fieldErrors[errorKey] || {}

                      return (
                        <div key={`${day}-${intervalIndex}`} className="weekday-interval-row">
                          <div className="weekday-schedule">
                            <div className="weekday-schedule-field">
                              <DateField
                                name={`${day}-${intervalIndex}-startTime`}
                                placeholderText={t('reservations:setupAvailabilityModal.startTime')}
                                value={interval.startTime || ''}
                                onChange={(e) =>
                                  onStartTimeChange(day, intervalIndex, e.target.value)
                                }
                                minTime={
                                  prevInterval?.endTime
                                    ? prevInterval.endTime
                                    : startOfDay(new Date())
                                }
                                maxTime={
                                  interval.endTime
                                    ? subMinutes(
                                        interval.endTime,
                                        values.timeInterval || DEFAULT_TIME_INTERVAL,
                                      )
                                    : endOfDay(new Date())
                                }
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={values.timeInterval || DEFAULT_TIME_INTERVAL}
                                timeCaption={t('reservations:setupAvailabilityModal.startTime')}
                                dateFormat="hh:mm a"
                                onChangeRaw={dateChangeRaw}
                                isError={!!intervalErrors.startTime}
                              />
                            </div>
                            <div className="weekday-schedule-field">
                              <DateField
                                name={`${day}-${intervalIndex}-endTime`}
                                placeholderText={t('reservations:setupAvailabilityModal.endTime')}
                                value={interval.endTime || ''}
                                onChange={(e) =>
                                  handleTimeChange(day, intervalIndex, 'endTime', e.target.value)
                                }
                                minTime={
                                  interval.startTime
                                    ? addMinutes(
                                        interval.startTime,
                                        values.timeInterval || DEFAULT_TIME_INTERVAL,
                                      )
                                    : startOfDay(new Date())
                                }
                                maxTime={endOfDay(new Date())}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={values.timeInterval || DEFAULT_TIME_INTERVAL}
                                injectTimes={[getMidnightTime()]}
                                timeCaption={t('reservations:setupAvailabilityModal.endTime')}
                                dateFormat="hh:mm a"
                                onChangeRaw={dateChangeRaw}
                                isError={!!intervalErrors.endTime}
                              />
                            </div>
                            <div className="price-field">
                              <TextField
                                name={`${day}-${intervalIndex}-price`}
                                placeholder={t(
                                  'reservations:amenityForms.pricingSelectors.enterPriceNoSymbolPlaceholder',
                                )}
                                min={0}
                                value={interval.price || ''}
                                onChange={(e) =>
                                  onChangeNumberField(e, (e) =>
                                    handlePriceChange(day, intervalIndex, e.target.value),
                                  )
                                }
                                type="number"
                                isError={!!intervalErrors.price}
                              />
                              <span className="price-prefix">$</span>
                            </div>
                          </div>
                          {isFirstInterval ? (
                            <button
                              type="button"
                              className="button button-icon button-add-interval-icon"
                              onClick={() => handleAddInterval(day)}
                              title={t('reservations:amenityForms.pricingSelectors.addInterval')}
                            >
                              <Plus size={20} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="button button-icon button-delete-interval"
                              onClick={() => handleRemoveInterval(day, intervalIndex)}
                              title={t('reservations:amenityForms.pricingSelectors.removeInterval')}
                            >
                              <Trash size={20} />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="weekday-unavailable">
                    <span>{t('reservations:amenityForms.pricingSelectors.unavailable')}</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {errors.hourlySlots && <div className="form-step__error">{errors.hourlySlots}</div>}
      </div>
    </div>
  )
}

export default HourlyPricingSelector
